import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import {
  MagnifyingGlassIcon,
  DocumentTextIcon,
  CalendarIcon,
  BuildingOfficeIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  PaperAirplaneIcon,
  ArrowDownTrayIcon,
  MapPinIcon
} from '@heroicons/react/24/outline';

interface Booking {
  bookingCode: string;
  customer: {
    firstName: string;
    lastName: string;
    email: string;
    phone?: {
      countryCode: string;
      number: string;
    };
  };
  package: {
    _id: string;
    name: string;
    resorts: any[];
  };
  selectedResort?: {
    _id: string;
    name: string;
    resortCode: string;
  };
  checkInDate?: string;
  checkOutDate?: string;
  status: 'voucher_purchased' | 'resort_selected' | 'confirmed' | 'completed' | 'cancelled';
  voucherExpiryDate: string;
  createdAt: string;
}

const BookingLookupPage: React.FC = () => {
  const [searchMethod, setSearchMethod] = useState<'booking' | 'email'>('booking');
  const [bookingId, setBookingId] = useState('');
  const [email, setEmail] = useState('');
  const [searchResult, setSearchResult] = useState<Booking | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState('');

  // Search for booking by booking code
  const searchByBookingCode = async () => {
    if (!bookingId.trim()) {
      setError('Please enter a booking code');
      return;
    }

    setIsSearching(true);
    setError('');
    setSearchResult(null);

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/bookings/code/${bookingId.trim().toUpperCase()}`
      );

      if (response.data && response.data.booking) {
        setSearchResult(response.data.booking);
      }
    } catch (err: any) {
      if (err.response?.status === 404) {
        setError('Booking not found. Please check your booking code and try again.');
      } else {
        setError('An error occurred while searching. Please try again later.');
      }
      console.error('Error searching booking:', err);
    } finally {
      setIsSearching(false);
    }
  };

  const handleSearch = async () => {
    if (searchMethod === 'booking') {
      await searchByBookingCode();
    } else {
      setError('Email search is not yet implemented. Please use booking code search.');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'confirmed':
        return 'bg-orange-100 text-orange-800';
      case 'resort_selected':
        return 'bg-amber-100 text-amber-800';
      case 'voucher_purchased':
        return 'bg-blue-100 text-blue-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon className="h-5 w-5 text-green-600" />;
      case 'confirmed':
        return <BuildingOfficeIcon className="h-5 w-5 text-orange-600" />;
      case 'resort_selected':
        return <CalendarIcon className="h-5 w-5 text-amber-600" />;
      case 'voucher_purchased':
        return <ClockIcon className="h-5 w-5 text-blue-600" />;
      case 'cancelled':
        return <ExclamationTriangleIcon className="h-5 w-5 text-red-600" />;
      default:
        return <DocumentTextIcon className="h-5 w-5 text-gray-600" />;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'voucher_purchased':
        return 'Voucher Purchased';
      case 'resort_selected':
        return 'Resort Selected';
      case 'confirmed':
        return 'Booking Confirmed';
      case 'completed':
        return 'Completed';
      case 'cancelled':
        return 'Cancelled';
      default:
        return status;
    }
  };

  const isVoucherExpired = (expiry: string) => {
    return new Date() > new Date(expiry);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <>
      <Helmet>
        <title>Booking Lookup - Find Your Booking | Bali Getaway</title>
        <meta name="description" content="Look up your Bali Getaway booking details using your booking code or email address." />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-yellow-50 to-orange-50">
        {/* Hero Section */}
        <div className="relative bg-gradient-to-br from-yellow-500 to-orange-600 py-16">
          <div className="absolute inset-0 bg-black bg-opacity-20"></div>
          <div className="absolute inset-0">
            <img 
              src="https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80" 
              alt="Bali landscape" 
              className="w-full h-full object-cover opacity-20"
            />
          </div>
          <div className="relative container mx-auto px-4 text-center">
            <div className="flex items-center justify-center mb-6">
              <img src="/logo.png" alt="Bali Getaway" className="h-16 w-auto mr-4" />
              <h1 className="text-4xl md:text-5xl font-bold text-white">Booking Lookup</h1>
            </div>
            <p className="text-xl text-white mb-8 max-w-2xl mx-auto">
              Find your booking details and manage your Bali adventure
            </p>
          </div>
        </div>

        {/* Search Section */}
        <div className="container mx-auto px-4 py-16">
          <div className="max-w-2xl mx-auto">
            <div className="bg-white rounded-2xl shadow-xl border border-yellow-200/50 overflow-hidden">
              <div className="bg-gradient-to-r from-yellow-500 to-orange-600 px-6 py-4">
                <h2 className="text-xl font-bold text-white flex items-center">
                  <MagnifyingGlassIcon className="h-6 w-6 mr-3" />
                  Search Your Booking
                </h2>
              </div>

              <div className="p-6">
                {/* Search Method Toggle */}
                <div className="flex bg-gray-100 rounded-lg p-1 mb-6">
                  <button
                    onClick={() => setSearchMethod('booking')}
                    className={`flex-1 py-2 px-4 rounded-md font-medium transition-all duration-200 ${
                      searchMethod === 'booking'
                        ? 'bg-yellow-500 text-white shadow-lg'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    Booking Code
                  </button>
                  <button
                    onClick={() => setSearchMethod('email')}
                    className={`flex-1 py-2 px-4 rounded-md font-medium transition-all duration-200 ${
                      searchMethod === 'email'
                        ? 'bg-yellow-500 text-white shadow-lg'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    Email Address
                  </button>
                </div>

                {/* Search Form */}
                <div className="space-y-4">
                  {searchMethod === 'booking' ? (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Booking Code
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          value={bookingId}
                          onChange={(e) => setBookingId(e.target.value)}
                          placeholder="Enter your booking code (e.g., BG20241201001)"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                          onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                        />
                        <DocumentTextIcon className="absolute right-3 top-3 h-6 w-6 text-gray-400" />
                      </div>
                    </div>
                  ) : (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email Address
                      </label>
                      <div className="relative">
                        <input
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="Enter your email address"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                          onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                        />
                        <PaperAirplaneIcon className="absolute right-3 top-3 h-6 w-6 text-gray-400" />
                      </div>
                    </div>
                  )}

                  <button
                    onClick={handleSearch}
                    disabled={isSearching}
                    className="w-full bg-gradient-to-r from-yellow-500 to-orange-600 text-white font-semibold py-3 px-6 rounded-lg hover:from-yellow-600 hover:to-orange-700 transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                  >
                    {isSearching ? (
                      <div className="flex items-center justify-center">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                        Searching...
                      </div>
                    ) : (
                      <div className="flex items-center justify-center">
                        <MagnifyingGlassIcon className="h-5 w-5 mr-2" />
                        Search Booking
                      </div>
                    )}
                  </button>
                </div>

                {/* Error Message */}
                {error && (
                  <div className="mt-4 p-4 bg-red-50 border-l-4 border-red-400 rounded">
                    <div className="flex">
                      <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
                      <p className="ml-3 text-sm text-red-700">{error}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Search Results */}
          {searchResult && (
            <div className="mt-8 max-w-4xl mx-auto">
              <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                <div className="bg-gradient-to-r from-yellow-500 to-orange-600 px-6 py-4">
                  <div className="flex items-center justify-between">
                    <div className="text-white">
                      <div className="flex items-center mb-2">
                        <img src="/logo.png" alt="Bali Getaway" className="h-8 w-auto mr-3" />
                        <h3 className="text-lg font-semibold">Booking Details</h3>
                      </div>
                      <p className="text-yellow-100">Customer: {searchResult.customer.firstName} {searchResult.customer.lastName}</p>
                      <p className="text-yellow-100">Email: {searchResult.customer.email}</p>
                      <p className="text-yellow-100">Booking Code: {searchResult.bookingCode}</p>
                    </div>
                    <div className="bg-white bg-opacity-20 backdrop-blur-sm rounded-lg px-4 py-2">
                      <div className="flex items-center space-x-2">
                        {getStatusIcon(searchResult.status)}
                        <span className="text-white font-medium">
                          {getStatusText(searchResult.status)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Package Information */}
                    <div className="space-y-4">
                      <div className="flex items-center space-x-2">
                        <DocumentTextIcon className="h-5 w-5 text-gray-400" />
                        <h4 className="font-semibold text-gray-900">Package Information</h4>
                      </div>
                      
                      <div className="bg-yellow-50 rounded-lg p-4 space-y-2 border border-yellow-200">
                        <p><span className="font-medium">Package:</span> {searchResult.package.name}</p>
                        <p><span className="font-medium">Available Resorts:</span> {searchResult.package.resorts.length} resorts</p>
                      </div>
                    </div>

                    {/* Booking Timeline */}
                    <div className="space-y-4">
                      <div className="flex items-center space-x-2">
                        <CalendarIcon className="h-5 w-5 text-gray-400" />
                        <h4 className="font-semibold text-gray-900">Timeline</h4>
                      </div>
                      
                      <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                        <p><span className="font-medium">Purchase Date:</span> {formatDate(searchResult.createdAt)}</p>
                        <p><span className="font-medium">Voucher Expiry:</span> {formatDate(searchResult.voucherExpiryDate)}</p>
                        {isVoucherExpired(searchResult.voucherExpiryDate) && (
                          <div className="flex items-center space-x-2 text-red-600">
                            <ExclamationTriangleIcon className="h-4 w-4" />
                            <span className="text-sm font-medium">Voucher Expired</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Resort Information */}
                  {searchResult.selectedResort ? (
                    <div className="mt-6">
                      <div className="flex items-center space-x-2 mb-4">
                        <BuildingOfficeIcon className="h-5 w-5 text-gray-400" />
                        <h4 className="font-semibold text-gray-900">Selected Resort</h4>
                      </div>
                      
                      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <p className="font-semibold text-green-900">{searchResult.selectedResort.name}</p>
                            <p className="text-green-700">Resort Code: {searchResult.selectedResort.resortCode}</p>
                          </div>
                          {searchResult.checkInDate && searchResult.checkOutDate && (
                            <div className="space-y-2">
                              <div className="flex items-center space-x-2">
                                <CalendarIcon className="h-4 w-4 text-green-600" />
                                <span className="text-sm font-medium text-green-900">Check-in: {formatDate(searchResult.checkInDate)}</span>
                              </div>
                              <div className="flex items-center space-x-2">
                                <CalendarIcon className="h-4 w-4 text-green-600" />
                                <span className="text-sm font-medium text-green-900">Check-out: {formatDate(searchResult.checkOutDate)}</span>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ) : searchResult.status === 'voucher_purchased' && (
                    <div className="mt-6">
                      <div className="flex items-center space-x-2 mb-4">
                        <BuildingOfficeIcon className="h-5 w-5 text-gray-400" />
                        <h4 className="font-semibold text-gray-900">Available Resorts</h4>
                      </div>
                      
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <p className="text-blue-800 mb-3">Choose from these available resorts for your package:</p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                          {searchResult.package.resorts.map((resort: any, index: number) => (
                            <div key={index} className="bg-white rounded-lg p-3 border border-blue-200">
                              <span className="text-blue-900 font-medium">{resort.name || `Resort ${index + 1}`}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Status Information */}
                  <div className="mt-6">
                    <div className="bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-200 rounded-lg p-4">
                      <div className="flex items-center space-x-2 mb-2">
                        <MapPinIcon className="h-5 w-5 text-yellow-600" />
                        <h4 className="font-semibold text-gray-900">Booking Status</h4>
                      </div>
                      <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(searchResult.status)}`}>
                        <span>{getStatusText(searchResult.status)}</span>
                      </div>
                      {searchResult.status === 'voucher_purchased' && (
                        <p className="text-sm text-gray-600 mt-2">
                          Next step: Select your preferred resort and travel dates to complete your booking.
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="mt-6 flex flex-wrap gap-3">
                    <button className="inline-flex items-center px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors">
                      <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
                      Download Voucher
                    </button>
                    
                    <button className="inline-flex items-center px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors">
                      <PaperAirplaneIcon className="h-4 w-4 mr-2" />
                      Contact Support
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default BookingLookupPage;
